import React, { useState, useEffect } from 'react';
import Pagination from '@mui/material/Pagination';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { FormattedMessage } from 'react-intl';
import Utils from '../Common/Utils.js';
import CopyableId from '../Common/CopyableId.js';
import { userIsSuperAdmin } from '../../Controller/userRoles.js';
import patientMetricDescriptors from './patientMetricDescriptors.js';
import '../components-style.css';

// Card layout of the patient list for phones and portrait tablets (< 900px).
// Presentational only: rows, alert thresholds and every action (profile modal,
// graph popup, TLS validation) come from PatientSimpleList via props, so the
// behavior stays identical to the DataGrid view.

const PAGE_SIZE = 20;

const deadlineStyle = (deadline) => {
    const daysLeft = (new Date(deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    if (daysLeft <= 0) return { color: '#DB2828', fontWeight: 'bold' };
    if (daysLeft <= 5) return { color: '#f57c00', fontWeight: 'bold' };
    if (daysLeft <= 10) return { color: '#f57c00', fontWeight: 'normal' };
    return { color: '#4caf50', fontWeight: 'normal' };
};

const checkIconColor = (row) => {
    if (row.is_checked) return '#4caf50';
    if (!row.check_patient_deadline) return '#bdbdbd';
    const daysLeft = (new Date(row.check_patient_deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    if (daysLeft <= 0) return '#DB2828';
    if (daysLeft <= 10) return '#f57c00';
    return '#4caf50';
};

// Same coloring rules as the corresponding renderCell in PatientSimpleList.js
const chipStyle = (desc, value, alerts) => {
    let color = '#333';
    let backgroundColor = 'transparent';
    const alert = desc.alertKey && alerts ? alerts[desc.alertKey] : null;
    if (desc.colorMode === 'full' && alert) {
        color = Utils.dataAlertColor(value, desc.field, alert.threshold, alert.threshold2);
        if (alert.activated === true && color === '#DB2828') backgroundColor = 'yellow';
    } else if (desc.colorMode === 'bgOnly' && alert) {
        if (alert.triggered === true) backgroundColor = 'yellow';
    } else if (desc.colorMode === 'fixedGreen') {
        color = value === '-' ? '#767676' : '#21BA45';
        if (alert && alert.activated === true && Utils.dataAlertColor(value, desc.field, alert.threshold, alert.threshold2) === '#DB2828') backgroundColor = 'yellow';
    }
    return { color, backgroundColor };
};

const sortRows = (rows, sortBy) => {
    const sorted = [...rows];
    if (sortBy === 'name') {
        sorted.sort((a, b) => String(a.patientName).localeCompare(String(b.patientName), undefined, { sensitivity: 'base' }));
    } else if (sortBy === 'tlsDeadline') {
        // most urgent first, patients without a deadline last
        sorted.sort((a, b) => {
            const da = a.check_patient_deadline ? new Date(a.check_patient_deadline).getTime() : null;
            const db = b.check_patient_deadline ? new Date(b.check_patient_deadline).getTime() : null;
            if (da === null && db === null) return 0;
            if (da === null) return 1;
            if (db === null) return -1;
            return da - db;
        });
    } else {
        // active alerts desc, nulls last — same order as the grid default
        sorted.sort((a, b) => {
            const aNull = a.alertCount == null;
            const bNull = b.alertCount == null;
            if (aNull && bNull) return 0;
            if (aNull) return 1;
            if (bNull) return -1;
            return b.alertCount - a.alertCount;
        });
    }
    return sorted;
};

const PatientCardList = (props) => {
    const {
        rows, getPatientAlerts, onOpenProfile, onOpenGraph, onRequestCheckPatient, onVerifyAll,
        isTlsAuthorizedForPatient, showTlsCols, allChecked,
        subscriptionFeatures, accountInformation, hidePatientIds, intl,
    } = props;

    const [sortBy, setSortBy] = useState('alerts');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    useEffect(() => { setPage(1); }, [search, sortBy]);

    const descriptors = patientMetricDescriptors.filter(d =>
        Utils.checkBit(subscriptionFeatures, Utils.featureToBitNumber(d.featureName)));

    const filtered = search
        ? rows.filter(r => String(r.patientName).toLowerCase().includes(search.toLowerCase()))
        : rows;
    const sorted = sortRows(filtered, sortBy);
    const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount);
    const pageRows = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    const descLabel = (desc) => desc.labelId
        ? intl.formatMessage({ id: desc.labelId, defaultMessage: desc.defaultLabel })
        : desc.defaultLabel;

    const renderTlsLine = (row) => {
        if (!showTlsCols) return null;
        const last = row.check_patient_last ? new Date(row.check_patient_last) : null;
        const userName = row.check_patient_user_name || '';
        const structureName = row.check_patient_structure_name || '';
        const nameParts = userName.trim().split(/\s+/);
        const initials = nameParts.length >= 2
            ? (nameParts[0][0] + '.' + nameParts[nameParts.length - 1][0]).toUpperCase()
            : (nameParts[0] ? nameParts[0][0].toUpperCase() : '');
        let deadlineEl = null;
        if (row.check_patient_deadline) {
            const deadline = new Date(row.check_patient_deadline);
            const daysLeft = (deadline.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
            const daysRounded = Math.ceil(Math.abs(daysLeft));
            const tooltipText = daysLeft <= 0
                ? intl.formatMessage({ id: 'patient.list.deadlineTooltipOverdue', defaultMessage: 'TLS overdue by {days}d' }, { days: daysRounded })
                : intl.formatMessage({ id: 'patient.list.deadlineTooltip', defaultMessage: 'Next TLS maximum in {days}d' }, { days: daysRounded });
            deadlineEl = (
                <Tooltip title={tooltipText} placement="top">
                    <span style={{ ...deadlineStyle(row.check_patient_deadline), fontSize: 12 }}>
                        → {deadline.toLocaleDateString()}
                    </span>
                </Tooltip>
            );
        }
        return (
            <div className="patient-card-tls">
                <span style={{ fontSize: 12, color: '#444' }}>
                    TLS&nbsp;:{' '}
                    {last
                        ? <>
                            {last.toLocaleDateString()} {last.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            {initials && (
                                <Tooltip title={structureName ? `${userName} (${structureName})` : userName} placement="top">
                                    <span style={{ fontSize: 11, color: '#888', fontWeight: 'bold' }}> {initials}</span>
                                </Tooltip>
                            )}
                        </>
                        : '-'}
                    {' '}{deadlineEl}
                </span>
                {isTlsAuthorizedForPatient(row) && (
                    !row.monitoringActive ? (
                        <Tooltip title={intl.formatMessage({ id: 'patient.list.tlsNoActiveMonitoringTooltip', defaultMessage: 'TLS not possible: the patient has no active monitoring' })} placement="top">
                            <span>
                                <Button size="small" variant="outlined" disabled
                                    startIcon={<CheckCircleIcon sx={{ color: '#DB2828' }} />}
                                    style={{ textTransform: 'none', fontSize: 11 }}
                                >
                                    <FormattedMessage id="patient.list.validateTls" defaultMessage="Valider TLS" />
                                </Button>
                            </span>
                        </Tooltip>
                    ) : (
                        <Button size="small" variant="outlined"
                            onClick={() => onRequestCheckPatient(row.id_patient)}
                            startIcon={<CheckCircleIcon sx={{ color: checkIconColor(row) }} />}
                            style={{ textTransform: 'none', fontSize: 11 }}
                        >
                            <FormattedMessage id="patient.list.validateTls" defaultMessage="Valider TLS" />
                        </Button>
                    )
                )}
            </div>
        );
    };

    const renderCard = (row) => {
        const alerts = getPatientAlerts(row.id_patient);
        let birthdateStr = '';
        if (!accountInformation.anonymised_patient && row.patientBirthdate) {
            const d = new Date(row.patientBirthdate);
            if (!isNaN(d.getTime())) birthdateStr = d.toLocaleDateString('fr-FR');
        }
        const showUserName = accountInformation.readOnly !== true && accountInformation.anonymised_patient !== true;
        return (
            <div className="patient-card" key={row.id_patient}>
                <div className="patient-card-header">
                    <div className="patient-card-name" onClick={() => onOpenProfile(row.id_patient)}>
                        {!row.monitoringActive && (
                            <span title="Suivi inactif" style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f44336', flexShrink: 0, boxShadow: '0 0 4px rgba(244,67,54,0.6)' }} />
                        )}
                        <span>{row.patientName}</span>
                        {userIsSuperAdmin(accountInformation) && !hidePatientIds && (
                            <CopyableId value={row.id_patient} label="id_patient" />
                        )}
                    </div>
                    <Tooltip title={intl.formatMessage({ id: 'patient.alertCount', defaultMessage: 'Alertes en cours' })} placement="top">
                        <span style={{
                            color: row.alertCount > 0 ? '#DB2828' : '#767676',
                            fontWeight: row.alertCount > 0 ? 'bold' : 'normal',
                            fontSize: 15,
                            whiteSpace: 'nowrap',
                        }}>
                            {row.alertCount > 0 ? `⚠ ${row.alertCount}` : '-'}
                        </span>
                    </Tooltip>
                </div>
                <div className="patient-card-subline">
                    {birthdateStr && (
                        <span style={{ fontSize: 11, color: '#546e7a', backgroundColor: '#e8f0f7', borderRadius: 10, padding: '2px 8px', letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>
                            {birthdateStr}
                        </span>
                    )}
                    {showUserName && row.userName && (
                        <span style={{ fontSize: 11, color: '#767676', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.userName}</span>
                    )}
                </div>
                {renderTlsLine(row)}
                <div className="patient-card-chips">
                    {descriptors.map(desc => {
                        const value = row[desc.field] !== undefined ? row[desc.field] : '-';
                        const display = desc.formatKey ? Utils.formatDataDisplay(desc.formatKey, value) : value;
                        return (
                            <Tooltip title={descLabel(desc)} placement="top" key={desc.field}>
                                <div
                                    className="patient-metric-chip"
                                    style={{ ...chipStyle(desc, value, alerts), cursor: desc.tappable ? 'pointer' : 'default' }}
                                    onClick={desc.tappable ? () => onOpenGraph(desc.field, row.id_patient) : undefined}
                                >
                                    {desc.renderIcon()}
                                    <span className="patient-metric-chip-value">{display}</span>
                                </div>
                            </Tooltip>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div>
            <div className="patient-card-toolbar">
                <TextField
                    select
                    size="small"
                    label={intl.formatMessage({ id: 'patient.list.sortBy', defaultMessage: 'Trier par' })}
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={{ minWidth: 150, backgroundColor: 'white' }}
                >
                    <MenuItem value="alerts">{intl.formatMessage({ id: 'patient.list.sortAlerts', defaultMessage: 'Alertes en cours' })}</MenuItem>
                    <MenuItem value="name">{intl.formatMessage({ id: 'patient.list.sortName', defaultMessage: 'Nom' })}</MenuItem>
                    {showTlsCols && <MenuItem value="tlsDeadline">{intl.formatMessage({ id: 'patient.list.sortTlsDeadline', defaultMessage: 'Échéance TLS' })}</MenuItem>}
                </TextField>
                <TextField
                    size="small"
                    placeholder={intl.formatMessage({ id: 'patient.list.searchPatient', defaultMessage: 'Rechercher un patient…' })}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ flex: 1, minWidth: 130, backgroundColor: 'white' }}
                />
                {showTlsCols && pageRows.some(row => Boolean(row.lpp_code)) && (
                    <Tooltip title={intl.formatMessage({ id: 'patient.list.verifyAllTooltip', defaultMessage: 'All pneumology-monitored patients on this page will be validated' })} placement="top">
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => onVerifyAll(pageRows)}
                            startIcon={<CheckCircleIcon sx={{ color: allChecked ? '#4caf50' : '#bdbdbd' }} />}
                            style={{ textTransform: 'none', fontSize: 12, backgroundColor: 'white' }}
                        >
                            <FormattedMessage id="patient.list.verify" defaultMessage="Validation TLS" />
                            {' — '}
                            <FormattedMessage id="patient.list.verifyAll" defaultMessage="Tout" />
                        </Button>
                    </Tooltip>
                )}
            </div>
            {sorted.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#767676', padding: 24 }}>
                    <FormattedMessage id="profileList.noProfile" defaultMessage="Aucun patient" />
                </div>
            ) : (
                <div className="patient-card-grid">
                    {pageRows.map(renderCard)}
                </div>
            )}
            {pageCount > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                    <Pagination
                        sx={{ '& .MuiPaginationItem-root': { backgroundColor: 'white' } }}
                        variant="outlined"
                        count={pageCount}
                        page={safePage}
                        onChange={(e, value) => setPage(value)}
                    />
                </div>
            )}
        </div>
    );
};

export default PatientCardList;
